// Cross-surface links. The four browser surfaces live on sibling subdomains of one
// registrable domain — website at the apex, plus app./merchant./admin. — so their
// origins are DERIVED from the current host (INF-1 tenant-blind: a redeploy on any
// domain just works, no code change). On localhost there are no real subdomains, so
// explicit VITE_*_URL overrides fill in (null when unset). Duplicated per surface
// (CB-D3) — no shared ui/ package at launch.

export type Surface = 'website' | 'app' | 'merchant' | 'admin';

function isLocalHost(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
}

// The apex (website) host for the current deployment: strip a known surface prefix.
function apexHost(host: string): string {
  return host.replace(/^(app|merchant|admin)\./, '');
}

function localOverride(surface: Surface): string | null {
  const map: Record<Surface, string | undefined> = {
    website: import.meta.env.VITE_WEBSITE_URL,
    app: import.meta.env.VITE_APP_URL,
    merchant: import.meta.env.VITE_MERCHANT_URL,
    admin: import.meta.env.VITE_ADMIN_URL,
  };
  return map[surface] ?? null;
}

// The origin (scheme + host, no trailing slash) of a sibling surface, or null when
// it cannot be resolved (localhost with no override).
export function surfaceOrigin(surface: Surface): string | null {
  if (typeof window === 'undefined') return null;
  const { hostname, protocol } = window.location;
  if (isLocalHost(hostname)) return localOverride(surface);
  const apex = apexHost(hostname);
  const prefix = surface === 'website' ? '' : `${surface}.`;
  return `${protocol}//${prefix}${apex}`;
}

// A page on a sibling surface: its origin (above) + a path. Null when the origin
// cannot be resolved (localhost with no override). The website's account menu and
// sheets link into the customer dashboard through this, never a literal host.
export function surfaceUrl(surface: Surface, path: string): string | null {
  const origin = surfaceOrigin(surface);
  return origin ? `${origin}${path.startsWith('/') ? path : `/${path}`}` : null;
}

// The surface a given role lands on after signing in — the whole point of a single
// login page: one entry, role-based destination.
export function surfaceForRole(role: string): Surface {
  if (role === 'admin') return 'admin';
  if (role === 'merchant' || role === 'staff') return 'merchant';
  return 'app';
}

// A destination the account can land on after login, derived from one held role.
// `role` is what switch-role must be called with to enter `surface`.
export interface LandingOption {
  surface: Surface;
  role: string;
}

// The DISTINCT surfaces an account can land on, from its full held-role set (GET /me
// `roles[]`) — the input to the login "Continue as…" chooser. merchant and staff
// collapse to the single merchant surface (staff only if merchant is absent); customer →
// app, admin → admin. Order is stable (customer, merchant, admin) so the chooser reads
// the same every time. One option ⇒ land directly; two or more ⇒ prompt.
export function landingOptions(roles: readonly string[]): LandingOption[] {
  const opts: LandingOption[] = [];
  if (roles.includes('customer')) opts.push({ surface: 'app', role: 'customer' });
  if (roles.includes('merchant') || roles.includes('staff')) {
    opts.push({ surface: 'merchant', role: roles.includes('merchant') ? 'merchant' : 'staff' });
  }
  if (roles.includes('admin')) opts.push({ surface: 'admin', role: 'admin' });
  return opts;
}

// The central login URL (website `/login`), carrying an optional post-login return
// target. Apps redirect here when they have no session.
export function centralLoginUrl(next?: string): string {
  const origin = surfaceOrigin('website') ?? '';
  const base = `${origin}/login`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

// True when `url` is an absolute link to a page on `surface`. The central login uses
// it to tell a merchant-dashboard bounce (`?next=` on the merchant host) from a customer
// sign-in, so it can show the merchant design. False when the origin is unknown
// (localhost with no override).
export function isSurfaceUrl(surface: Surface, url: string | null): boolean {
  const origin = surfaceOrigin(surface);
  if (!url || !origin || !/^https?:\/\//.test(url)) return false;
  try {
    return new URL(url).origin === origin;
  } catch {
    return false;
  }
}

// Resolve where to send a user after a successful login. Honors `next` only when it
// points at the role's own surface (a deep link back after that surface bounced
// them) — never an arbitrary origin (open-redirect guard) or the wrong surface.
export function postLoginUrl(role: string, next: string | null): string {
  const target = surfaceForRole(role);
  const origin = surfaceOrigin(target);
  if (next && origin) {
    try {
      if (new URL(next, origin).origin === origin) return next;
    } catch {
      // fall through to the default landing
    }
  }
  return origin ? `${origin}/` : '/';
}
